import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { Box, Stack } from "@mui/material";
import { carriersGetCarriersOptions, useHeyApi } from "@platform/api";
import {
  AiFieldLookupProvider,
  CellReviewProvider,
  FieldMetadataProvider,
  PlanDesignerProvider,
  PlanOptionEditorProvider,
  ValidationProvider,
} from "@platform/btx-plan-designer";
import {
  RatesGrid,
  SummaryGrid,
  type FootnoteTarget,
} from "@platform/btx-rates";
import { buildAiFieldsByPlanId } from "@platform/document-library";
import { PPX_QUOTE_HISTORY } from "@platform/feature-flags";
import { DialogProvider, NotificationProvider } from "@platform/hooks";
import {
  buildSuppressSeededDefaults,
  DifferenceCellStateSyncer,
  isUnmappedPageId,
  proposalQueryKeys,
  SbsCellStateProvider,
  useQuotesDifferenceMap,
  type ProposalScope,
} from "@platform/mpx-quotes-grid";
import { PlanOptionNameEnum } from "@platform/openapi-types";
import { CoverageTypeEnum } from "@platform/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { GridApi } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
} from "react";
import { useParams, useSearchParams } from "react-router";
import { Alert } from "@platform/design-system";

import { useExitFullScreenOnEscape } from "../../../../../../hooks/useExitFullScreenOnEscape";
import { useRfpDetails } from "../../../../../../hooks/useRfpDetails";
import useToastNotificationService from "../../../../../../hooks/useToastNotificationService";
import { useVisibleMpConfigKeys } from "../../../../../../hooks/useVisibleMpConfigKeys";
import { CellReviewCommentDialogRenderer } from "../shared/cellReviews/CellReviewCommentDialogRenderer";

import { AiCellStateSyncer } from "./components/AiCellStateSyncer";
import { ProposalExtractionToaster } from "./components/ProposalExtractionToaster";
import { QuotesDialogsAndDrawers } from "./components/QuotesDialogsAndDrawers";
import { QuotesFullScreenPopupStyles } from "./components/QuotesFullScreenPopupStyles";
import { getQuotesLoadingState } from "./components/QuotesLoadingState";
import { QuotesRatesErrorProvider } from "./components/QuotesRatesErrorProvider";
import { QuotesPlanEditorPanel } from "./components/QuotesPlanEditorPanel";
import { QuotesToolbar } from "./components/QuotesToolbar";
import { UnmappedPlansBannerSection } from "./components/UnmappedPlansBannerSection";
import { COMA_INCLUSION_ONLY_KEY } from "./formatting/quotesCellViewRenderer";
import { useAddPlanToGroupMutation } from "./hooks/useAddPlanToGroupMutation";
import { useAiCellStateMap } from "./hooks/useAiCellStateMap";
import { useBenefitPlanGroupFootnoteMutation } from "./hooks/useBenefitPlanGroupFootnoteMutation";
import { useElementHeight } from "./hooks/useElementHeight";
import { useFootnoteHeaderLabel } from "./hooks/useFootnoteHeaderLabel";
import { useIllustratedFrom } from "./hooks/useIllustratedFrom";
import { useMaterializeProposalMutation } from "./hooks/useMaterializeProposalMutation";
import { useProposalPlanBenefitsMutation } from "./hooks/useProposalPlanBenefitsMutation";
import { useProposalPlanGroupFootnoteMutation } from "./hooks/useProposalPlanGroupFootnoteMutation";
import { useQuotedEnrollmentReview } from "./hooks/useQuotedEnrollmentReview";
import { useQuoteHistory } from "./hooks/useQuoteHistory";
import { useQuoteHistoryView } from "./hooks/useQuoteHistoryView";
import { useQuotesCarrierOrdering } from "./hooks/useQuotesCarrierOrdering";
import { useQuotesCellReviews } from "./hooks/useQuotesCellReviews";
import { useQuotesData } from "./hooks/useQuotesData";
import { useQuotesFieldOverrides } from "./hooks/useQuotesFieldOverrides";
import { useQuotesGridStatePersistence } from "./hooks/useQuotesGridStatePersistence";
import { useQuotesHiddenColumns } from "./hooks/useQuotesHiddenColumns";
import { useQuotesOverrides } from "./hooks/useQuotesOverrides";
import { useRequestedPlanBenefitsMutation } from "./hooks/useRequestedPlanBenefitsMutation";
import { useRiderChargeEditor } from "./hooks/useRiderChargeEditor";
import {
  REQUESTED_CARRIER_ID,
  useQuotesPresentationGridData,
} from "./hooks/useQuotesPresentationGridData";
import { useRatesDrawerState } from "./hooks/useRatesDrawerState";
import QuotesLegend from "./QuotesLegend";
import {
  applyDependentToggleOverrides,
  captureDependentToggleOverrides,
  deriveHiddenDependentToggleOverrides,
} from "./utils/dependentToggleOverrides";
import {
  buildPopupCustomTextCellKey,
  parsePopupCustomTextCellKey,
  type JsonValue,
  type PopupCustomTextFieldIdentity,
} from "./utils/popupCustomText";
import {
  benefitFieldPath,
  resolveCustomTextEditedStamp,
} from "./utils/customTextEditedStamp";
import { buildCustomTextActivityLookup } from "./utils/customTextActivity";
import { materializeSaveErrorMessage } from "./utils/materializeSaveErrorMessage";
import {
  COLUMN_WIDTH_SCALE,
  getFullScreenOverlaySx,
  getStickyHeaderTop,
  getStickyToolbarSx,
  GRID_CONTAINER_SX,
  QUOTES_PLAN_EDITOR_SX,
} from "./utils/quotesGridStyles";
import {
  PLACEHOLDER_PLAN_ID_PREFIX,
  READ_ONLY_CARRIER_IDS,
} from "./utils/quotesPlanIds";

import type {
  ColumnWidthPreset,
  LocCoverageType,
  PlanOptionEditorApi,
  RfpBenefitPlan,
  RfpCoverage,
} from "@platform/btx-plan-designer";
import type { PlaceholderMaterializeTarget } from "@platform/mpx-quotes-grid";
import type { ProposalPlanLevelFields } from "./hooks/useMaterializeProposalMutation";

// ---------------------------------------------------------------------------
// Quotes (main) — unified SBS grid with carrier super-groups
// ---------------------------------------------------------------------------

const DISABLED_RATE_EDIT_TOOLTIP =
  "Enter a benefit value for this plan before editing rates";

// Module-level so the carrier-grouped column builder, which lists it as a
// dependency, doesn't rebuild every Quotes column on each render.
const CARRIER_GROUPED_BENEFITS_HEADER_LABELS = ["Carrier", "Version", "Plan"];

const Quotes = (): React.JSX.Element => {
  const { id: rfpId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCoverageType =
    (searchParams.get("loc") as CoverageTypeEnum | null) ?? undefined;
  const initialBpgId = searchParams.get("bpg") ?? undefined;
  const planEditorGridRef = useRef<AgGridReact>(null);
  const [planEditorApi, setPlanEditorApi] = useState<GridApi | null>(null);
  const ratesGridRef = useRef<AgGridReact>(null);
  const summaryGridRef = useRef<AgGridReact>(null);
  const [showLegend, setShowLegend] = useState(true);
  const quoteHistoryEnabled = useFeatureIsOn(PPX_QUOTE_HISTORY);
  // Held above the proposal fetch below, which needs the read scope this picks.
  const {
    effectiveShowHistory,
    selectedHistoryCarrierIds,
    enterHistory,
    exitHistory,
  } = useQuoteHistoryView(quoteHistoryEnabled);
  // Gate all requote/quote-history data behind the flag: off → `deliverable`
  // (active proposals only), so no pending-requote or version columns — and
  // their styling — can render.
  const proposalScope: ProposalScope = !quoteHistoryEnabled
    ? "deliverable"
    : effectiveShowHistory
      ? "all"
      : "board";
  const [showUploadDocumentsDrawer, setShowUploadDocumentsDrawer] =
    useState(false);
  const [columnWidth, setColumnWidth] = useState<ColumnWidthPreset>("medium");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const columnWidthScale = COLUMN_WIDTH_SCALE[columnWidth];
  const { ref: toolbarRef, height: toolbarHeight } = useElementHeight();

  useExitFullScreenOnEscape(isFullScreen, () => setIsFullScreen(false));
  const [planEditorColumnWidths, setPlanEditorColumnWidths] = useState<
    Record<string, number>
  >({});
  const removePlanEditorColumnResizedListener = useRef<(() => void) | null>(
    null,
  );

  useEffect(
    () => () => {
      removePlanEditorColumnResizedListener.current?.();
    },
    [],
  );

  const handlePlanEditorGridReady = useCallback((api: GridApi) => {
    setPlanEditorApi(api);
    removePlanEditorColumnResizedListener.current?.();
    const readWidths = (): void => {
      const widths: Record<string, number> = {};
      // A hidden column keeps its width in AG Grid; count it as 0 so a rates
      // or premiums column spanning a plan's networks narrows to the ones
      // still shown.
      api.getColumns()?.forEach((col) => {
        widths[col.getColId()] = col.isVisible() ? col.getActualWidth() : 0;
      });
      setPlanEditorColumnWidths((prev) => {
        const keys = Object.keys(widths);
        const unchanged =
          keys.length === Object.keys(prev).length &&
          keys.every((key) => prev[key] === widths[key]);
        return unchanged ? prev : widths;
      });
    };
    readWidths();
    // The carrier grid's columns render after gridReady (async data), so reading
    // once on ready captures an empty map. Re-read whenever columns are
    // displayed, sized, or resized so RatesGrid/SummaryGrid can align to
    // PlanEditor once its real column widths exist.
    const events = [
      "columnResized",
      "displayedColumnsChanged",
      "firstDataRendered",
      "gridSizeChanged",
    ] as const;
    const handler = (event: { type?: string; finished?: boolean }): void => {
      // columnResized fires continuously during a drag; only read on finish.
      if (event.type === "columnResized" && event.finished === false) return;
      readWidths();
    };
    events.forEach((eventType) => api.addEventListener(eventType, handler));
    removePlanEditorColumnResizedListener.current = () => {
      events.forEach((eventType) =>
        api.removeEventListener(eventType, handler),
      );
    };
  }, []);

  const brokerPortalRfpId = useMemo<number | undefined>(() => {
    if (!rfpId) return undefined;
    const parsed = Number(rfpId);
    return Number.isFinite(parsed) ? parsed : undefined;
  }, [rfpId]);

  const { createToastNotification } = useToastNotificationService();

  // Broker ID powers the proposal upload drawer rendered in this tab.
  const { data: brokerId } = useRfpDetails<number | undefined>({
    rfpId: rfpId ?? "",
    enabled: !!rfpId,
    select: (data) => data.brokerId,
  });
  // Carrier directory (shareIdentifier + display name) from the centro-portal
  // GET /carriers endpoint. The shareIdentifier (`id`) is what the proposal
  // upload sends as carrierIdentifier so BTX correlates to the invited carrier.
  // `GET /carriers` is the full (unscoped) directory, so only fetch it once the
  // upload drawer is open rather than on every Quotes-tab mount.
  const { centroPortalClient } = useHeyApi();
  const { data: carriers = [] } = useQuery({
    ...carriersGetCarriersOptions({ client: centroPortalClient }),
    enabled: showUploadDocumentsDrawer,
    staleTime: 5 * 60 * 1000,
    select: (data) =>
      [...data.carriers].sort((a, b) => a.name.localeCompare(b.name)),
  });

  const {
    axiosInstance,
    ecoRfpId,
    isLoading,
    isRefetchingProposals,
    error,
    hasData,
    selectedCoverage,
    selectedCoverageType,
    selectedBpgId,
    proposals,
    benefitPlanGroupsByCoverage,
    availableCoverageTypes,
    isLoadingAvailableCoverageTypes,
    unmappedCountByCoverage,
    carrierNamesWithUnmappedPlans,
    selectedBenefitPlanGroup,
    handlePlanSelect,
  } = useQuotesData(rfpId, proposalScope, initialCoverageType, initialBpgId);

  const {
    historyDialogOpen,
    isLoadingCarriers,
    setHistoryDialogOpen,
    historyCarrierOptions,
    gridProposals,
    handleHistoryButtonClick,
    prefetchHistoryProposals,
    handleHistoryView,
  } = useQuoteHistory({
    rfpId,
    ecoRfpId,
    selectedCoverageType,
    selectedBpgId,
    proposals,
    quoteHistoryEnabled,
    effectiveShowHistory,
    selectedHistoryCarrierIds,
    enterHistory,
    exitHistory,
  });

  const benefitPlanGroupsForCoverage = useMemo(
    () =>
      (
        (selectedCoverageType
          ? benefitPlanGroupsByCoverage[selectedCoverageType]
          : undefined) ?? []
      ).filter((group) => !isUnmappedPageId(group.id)),
    [benefitPlanGroupsByCoverage, selectedCoverageType],
  );

  // Move Plan dialog error toast — the mutation itself is wired inside
  // PlanEditorContent, which (unlike Quotes) renders as a descendant of
  // PlanDesignerProvider and can resolve the axios instance from its context.
  const handleMoveError = useCallback(
    (err: Error) => {
      createToastNotification({
        severity: "error",
        message: `Failed to move plan: ${err.message}`,
      });
    },
    [createToastNotification],
  );

  // Source document ids of every origin=document proposal across all carriers — one per
  // uploaded document, i.e. every SBS document column. Passed to the AI cell-state map so all
  // columns stay annotated (the ai-dox /proposals list is latest-only for the History tab).
  const btxDocumentIds = useMemo(
    () =>
      proposals.flatMap((proposal) =>
        proposal.origin === "document" && proposal.documentId
          ? [proposal.documentId]
          : [],
      ),
    [proposals],
  );

  const { cellStateMap, fieldLookup } = useAiCellStateMap(
    ecoRfpId,
    selectedCoverageType,
    btxDocumentIds,
  );

  const handlePlanSelectWithUrl = useCallback(
    (coverageType: CoverageTypeEnum, bpgId?: string) => {
      handlePlanSelect(coverageType, bpgId);
      // The history view belongs to the page being left: its carrier picker and board
      // filter are both built from that page's coverage and requested plan, so carrying
      // it over would filter the new page to carriers that mean nothing on it.
      exitHistory();
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("loc", coverageType);
          if (bpgId) {
            next.set("bpg", bpgId);
          } else {
            next.delete("bpg");
          }
          return next;
        },
        { replace: true },
      );
    },
    [handlePlanSelect, setSearchParams, exitHistory],
  );

  // The plan editor grid stays mounted across LOCs and keeps its horizontal
  // scroll, which would land the analyst mid-board on the next LOC. Scroll back
  // to the first carrier column (the incumbent, when there is one, since the
  // carrier sequence sorts it first); the aligned Rates and Summary grids
  // follow. The initial LOC is left alone.
  const previousCoverageTypeRef = useRef(selectedCoverageType);
  useEffect(() => {
    const previousCoverageType = previousCoverageTypeRef.current;
    previousCoverageTypeRef.current = selectedCoverageType;
    const isLocChange =
      previousCoverageType !== undefined &&
      previousCoverageType !== selectedCoverageType;
    if (!isLocChange || !planEditorApi) return;

    const [firstCarrierColumn] = planEditorApi.getDisplayedCenterColumns();
    if (firstCarrierColumn) {
      planEditorApi.ensureColumnVisible(firstCarrierColumn, "start");
    }
  }, [selectedCoverageType, planEditorApi]);

  // MPX-persisted grid state for the Quotes plan editor
  const quotesGridStatePropsBound = useQuotesGridStatePersistence(ecoRfpId);
  const { trackPlanEditorGrid, trackCompanionGrid } =
    useQuotesHiddenColumns(ecoRfpId);
  const handlePlanEditorGridReadyWithHiddenColumns = useCallback(
    (api: GridApi) => {
      handlePlanEditorGridReady(api);
      trackPlanEditorGrid(api);
    },
    [handlePlanEditorGridReady, trackPlanEditorGrid],
  );

  // Shared carrier sequence context (persists across tab navigation) — any
  // reorder is applied to all SBS pages
  const { carrierIdOrderForGrid, setCarrierOrder } = useQuotesCarrierOrdering({
    proposals,
    selectedCoverageType,
    effectiveShowHistory,
    createToastNotification,
  });

  // Compute grid data with carrier ordering applied
  const { carrierGridData, ratesGridData } = useQuotesPresentationGridData({
    proposals: gridProposals,
    selectedCoverageType,
    selectedBpgId,
    carrierIdOrderForGrid,
    selectedCoverage,
    selectedBenefitPlanGroup,
    benefitPlanGroupsForCoverage,
  });

  // Build a per-plan lookup of combined AI extraction metadata for the
  // RatesGrid indicator, reusing the fieldLookup already fetched for the
  // benefits-grid AI hints. `fieldLookup` is keyed by btxIdentifier — for
  // carrier columns that's the proposal plan id, and for the synthetic
  // Incumbent/Requested column it's the request-side benefit plan id.
  const aiFieldsByPlanId = useMemo(() => {
    const planIds =
      ratesGridData?.benefitPlanGroups.flatMap((group) =>
        group.benefitPlans.map((plan) => plan.id),
      ) ?? [];
    return buildAiFieldsByPlanId(fieldLookup, planIds);
  }, [fieldLookup, ratesGridData?.benefitPlanGroups]);

  // FE-only overrides for the Dependent Benefits Included toggle, keyed by plan
  // id (see captureDependentToggleOverrides). State (not a ref) so capturing a
  // choice re-renders and the overlay memo below re-applies it.
  const [dependentToggleOverrides, setDependentToggleOverrides] = useState<
    Record<string, string>
  >({});

  // Stable reference for the flattened benefit plan groups passed to PlanEditor.
  // Inline flatMap creates a new array every render; a new array reference
  // causes columnDefs to recompute, which re-asserts flex widths on AG Grid
  // columns and overwrites the user's manual column resize.
  const planEditorBenefitPlanGroups = useMemo(
    () =>
      carrierGridData?.carrierGroups.flatMap((cg) => cg.benefitPlanGroups) ??
      [],
    [carrierGridData?.carrierGroups],
  );

  // Overlay the analyst's captured benefits-included choices before PlanEditor
  // builds its rows, so a rebuild doesn't discard them.
  const planEditorCoverage = useMemo(
    () =>
      applyDependentToggleOverrides(
        carrierGridData?.coverage,
        dependentToggleOverrides,
      ),
    [carrierGridData?.coverage, dependentToggleOverrides],
  );

  // onCellValueChanged closes over the coverage captured when the grid options
  // were built. Without a live getter it keeps using a stale coverage after the
  // analyst switches coverage type, so the edited plan id can't be resolved.
  // Expose the current coverage via a ref so the edit handler always sees the
  // active coverage.
  const currentCoverageRef = useRef(planEditorCoverage);
  currentCoverageRef.current = planEditorCoverage;
  const getCurrentCoverage = useCallback(() => currentCoverageRef.current, []);

  // Synthesized placeholder plans have no real proposal_plan row, so a
  // field-overrides lookup for their synthetic id 400s. Exclude them — a
  // placeholder can't carry custom-text overrides until it's materialized.
  const proposalPlanIds = useMemo(
    () =>
      (carrierGridData?.coverage.benefitPlans ?? [])
        .map((plan) => plan.id)
        .filter(
          (id): id is string =>
            !!id && !id.startsWith(PLACEHOLDER_PLAN_ID_PREFIX),
        ),
    [carrierGridData?.coverage.benefitPlans],
  );

  const {
    customTextByCellKey: popupCustomTextByCellKey,
    onCustomTextChange: handlePopupCustomTextChange,
  } = useQuotesFieldOverrides({
    rfpId: ecoRfpId,
    coverageType: selectedCoverageType,
    proposalPlanIds,
    enabled: hasData,
    onSaveError: useCallback(
      (err: Error) => {
        createToastNotification({
          severity: "error",
          message: `Failed to save custom text: ${err.message}`,
        });
      },
      [createToastNotification],
    ),
  });

  const {
    getReview,
    upsertReview,
    clearReview,
    getBrokerHighlight,
    setBrokerHighlight,
    clearBrokerHighlight,
    getEdited,
    setEdited,
    getDismissedDifferenceValue,
    dismissDifference,
  } = useQuotesCellReviews({
    rfpId: ecoRfpId,
    coverageType: selectedCoverageType,
    enabled: hasData,
    onSaveError: useCallback(
      (err: Error) => {
        createToastNotification({
          severity: "error",
          message: `Failed to save cell review: ${err.message}`,
        });
      },
      [createToastNotification],
    ),
  });

  // Custom text is stored outside the grid's row values, so the difference map
  // learns of it here and leaves those cells unflagged.
  const hasCustomText = useMemo(
    () => buildCustomTextActivityLookup(popupCustomTextByCellKey),
    [popupCustomTextByCellKey],
  );

  const differenceMap = useQuotesDifferenceMap({
    api: planEditorApi,
    requestedCarrierId: REQUESTED_CARRIER_ID,
    carrierGridData,
    dismissedValueOf: getDismissedDifferenceValue,
    hasCustomText,
  });

  // The grid reports cells as (benefit row, plan column); the review store keys
  // them as (plan, benefit).
  const handleDismissDifference = useCallback(
    (benefitKey: string, planId: string, differenceValue: string | undefined) =>
      dismissDifference(planId, benefitKey, differenceValue),
    [dismissDifference],
  );

  const materializeErrorKindRef = useRef<"benefit" | "footnote">("benefit");
  const onMaterializeSaveError = useCallback(
    (err: Error) => {
      createToastNotification({
        severity: "error",
        message: materializeSaveErrorMessage(
          materializeErrorKindRef.current,
          err,
        ),
      });
    },
    [createToastNotification],
  );

  const { materialize } = useMaterializeProposalMutation({
    rfpId: ecoRfpId,
    axiosInstance,
    onSaveError: onMaterializeSaveError,
  });

  const { addPlanToGroup } = useAddPlanToGroupMutation({
    rfpId: ecoRfpId,
    axiosInstance,
    onSaveError: onMaterializeSaveError,
  });

  const materializeForBenefit = useCallback(
    (
      target: PlaceholderMaterializeTarget,
      benefits: RfpBenefitPlan["benefits"],
      planFields: ProposalPlanLevelFields,
      footnote?: string,
      onMaterialized?: (planId: string) => void,
    ) => {
      materializeErrorKindRef.current = "benefit";
      materialize(target, benefits, planFields, footnote, onMaterialized);
    },
    [materialize],
  );

  const materializeForFootnote = useCallback(
    (
      target: PlaceholderMaterializeTarget,
      benefits: RfpBenefitPlan["benefits"],
      planFields: ProposalPlanLevelFields,
      footnote?: string,
    ) => {
      materializeErrorKindRef.current = "footnote";
      materialize(target, benefits, planFields, footnote);
    },
    [materialize],
  );

  const {
    placeholderMaterializeByPlanId,
    padFillByPlanId,
    emptyBenefitPlanIds,
  } = carrierGridData ?? {};

  // The Incumbent/Requested column's plans — the RFP's own requested benefit
  // plans. Only their extended (non-v5) fields are editable (CD-2301), and
  // they save through the benefit-plan endpoint rather than a proposal plan's.
  const requestedBenefitPlanIds = useMemo(
    () =>
      new Set(
        (carrierGridData?.carrierGroups ?? [])
          .filter((carrier) => carrier.carrierId === REQUESTED_CARRIER_ID)
          .flatMap((carrier) =>
            carrier.benefitPlanGroups.flatMap((group) =>
              group.benefitPlans.map((plan) => plan.id),
            ),
          ),
      ),
    [carrierGridData],
  );

  const { saveRequestedPlanBenefits } = useRequestedPlanBenefitsMutation({
    ecoRfpId,
    axiosInstance,
    coverageType: selectedCoverageType as LocCoverageType | undefined,
    onSaveError: onMaterializeSaveError,
  });

  const { onCoverageChange: handleCoverageChange } =
    useProposalPlanBenefitsMutation({
      rfpId: ecoRfpId,
      axiosInstance,
      scope: proposalScope,
      coverageType: selectedCoverageType as LocCoverageType | undefined,
      placeholderMaterializeByPlanId,
      padFillByPlanId,
      emptyBenefitPlanIds,
      requestedBenefitPlanIds,
      saveRequestedPlanBenefits,
      materialize: materializeForBenefit,
      addPlanToGroup,
      onSaveError: onMaterializeSaveError,
      // Grid edits on a synthetic (pad/placeholder) column can only be
      // stamped "Edited" once the column has a real plan id; PlanEditorContent
      // skips them and this stamps them against the plan the save created.
      onNewPlanPersisted: (realPlanId, cells) => {
        for (const { benefitKey, network } of cells) {
          setEdited(realPlanId, benefitFieldPath(benefitKey, network));
        }
      },
    });

  const handleCoverageChangeWithToggleOverride = useCallback(
    (updatedCoverage: RfpCoverage) => {
      const updates = {
        ...captureDependentToggleOverrides(updatedCoverage),
        ...deriveHiddenDependentToggleOverrides(updatedCoverage),
      };
      if (Object.keys(updates).length > 0) {
        setDependentToggleOverrides((prev) => ({ ...prev, ...updates }));
      }
      const editedCoverage =
        applyDependentToggleOverrides(updatedCoverage, updates) ??
        updatedCoverage;
      // A fill-handle drag or multi-cell paste arrives as a run of cell edits
      // with no render between them, and each edit starts from
      // getCurrentCoverage(). Point the ref at this edit now, or the next one
      // starts from the coverage before it and its save reverts this cell.
      // The dirty markers describe this edit only, so they don't carry over.
      currentCoverageRef.current = {
        ...editedCoverage,
        _dirtyPlanIds: undefined,
        _dirtyCells: undefined,
      };
      handleCoverageChange(editedCoverage);
    },
    [handleCoverageChange],
  );

  // Placeholder/pad columns have no real proposal_plan row, so a custom-text
  // save against their synthetic id 400s the same way a benefit-field PATCH
  // would. Route through the same materialize/add-plan paths benefit edits
  // use, then retry the custom-text save against the resulting real plan id.
  const materializeForCustomText = useCallback(
    (planId: string, retry: (realPlanId: string) => void): boolean => {
      const padTarget = padFillByPlanId?.get(planId);
      if (padTarget) {
        addPlanToGroup(padTarget, undefined, {}, retry);
        return true;
      }
      const materializeTarget = placeholderMaterializeByPlanId?.get(planId);
      if (materializeTarget) {
        materialize(materializeTarget, undefined, {}, undefined, retry);
        return true;
      }
      return false;
    },
    [
      padFillByPlanId,
      placeholderMaterializeByPlanId,
      addPlanToGroup,
      materialize,
    ],
  );

  // The editors capture this handler when they mount, but cell reviews load
  // after the grid does — read the store through a ref so the stamp sees the
  // live value (same pattern as PlanEditorContent).
  const setEditedRef = useRef(setEdited);
  setEditedRef.current = setEdited;

  const handleCustomTextChangeMaterializing = useCallback(
    (cellKey: string, customText: string | null, originalValue?: JsonValue) => {
      // A custom-text save is persisted as a field override, bypassing AG
      // Grid's value-commit path — onCellValueChanged never marks the cell
      // dirty, so PlanEditorContent's coverage-change handler can't stamp the
      // "Edited" review. Stamp it here instead, like grid edits. A synthetic
      // column has no plan id the review store accepts until it materializes,
      // so its stamp waits for the real id the retry callback receives.
      const stamp =
        customText !== null ? resolveCustomTextEditedStamp(cellKey) : undefined;
      const identity = parsePopupCustomTextCellKey(cellKey);
      const materialized =
        identity &&
        materializeForCustomText(identity.benefitPlanId, (realPlanId) => {
          if (stamp) setEditedRef.current(realPlanId, stamp.fieldPath);
          const realCellKey = buildPopupCustomTextCellKey({
            ...identity,
            benefitPlanId: realPlanId,
          } as PopupCustomTextFieldIdentity);
          handlePopupCustomTextChange(realCellKey, customText, originalValue);
        });
      if (!materialized) {
        if (stamp) setEditedRef.current(stamp.planId, stamp.fieldPath);
        handlePopupCustomTextChange(cellKey, customText, originalValue);
      }
    },
    [materializeForCustomText, handlePopupCustomTextChange],
  );

  const onFootnoteSaveError = useCallback(
    (err: Error) => {
      createToastNotification({
        severity: "error",
        message: `Failed to save footnote: ${err.message}`,
      });
    },
    [createToastNotification],
  );

  const { saveFootnote } = useProposalPlanGroupFootnoteMutation({
    rfpId: ecoRfpId,
    axiosInstance,
    scope: proposalScope,
    onSaveError: onFootnoteSaveError,
  });

  const { saveFootnote: saveBenefitFootnote, saveFootnoteHeaderLabel } =
    useBenefitPlanGroupFootnoteMutation({
      ecoRfpId,
      axiosInstance,
      onSaveError: onFootnoteSaveError,
    });

  const onIllustratedFromSaveError = useCallback(
    (err: Error) => {
      createToastNotification({
        severity: "error",
        message: `Failed to save "illustrated from" source: ${err.message}`,
      });
    },
    [createToastNotification],
  );

  // A mutation instance of its own, so a failed save names the field it was for.
  const { saveIllustratedFrom } = useBenefitPlanGroupFootnoteMutation({
    ecoRfpId,
    axiosInstance,
    onSaveError: onIllustratedFromSaveError,
  });

  const handleFootnoteChange = useCallback(
    (target: FootnoteTarget, footnote: string) => {
      if (target.kind === "benefit") {
        saveBenefitFootnote(target.id, footnote);
      } else if (target.kind === "placeholder") {
        materializeForFootnote(target.target, undefined, {}, footnote);
      } else {
        saveFootnote(target.id, footnote);
      }
    },
    [saveFootnote, saveBenefitFootnote, materializeForFootnote],
  );

  const { footnoteHeaderLabel, onFootnoteHeaderLabelChange } =
    useFootnoteHeaderLabel({
      selectedBpgId,
      selectedBenefitPlanGroup,
      saveFootnoteHeaderLabel,
    });

  const illustratedFrom = useIllustratedFrom({
    selectedCoverageType,
    selectedBpgId,
    selectedBenefitPlanGroup,
    coverageBenefitPlanGroups: benefitPlanGroupsForCoverage,
    saveIllustratedFrom,
  });

  const queryClient = useQueryClient();
  // Rate mutations already write the new value straight into the
  // rate-schedule cache (see useUpdateProposalRate/useReplaceProposalRateSchedules),
  // which RatesGrid's cell renderers and SummaryGrid's premium calc both read
  // from — so the edited cell and premium totals update without a refetch.
  // Mark the proposals query stale (not refetched now) so the embedded
  // rateSchedule snapshot resyncs on the next natural refetch (e.g. a LOC
  // switch) instead of forcing one immediately: an immediate refetch here
  // would hand RatesGrid brand-new benefitPlanGroups/carrierGroups
  // references, rebuilding its columns and remounting every cell mid-edit.
  const handleRateSaved = useCallback(() => {
    if (ecoRfpId) {
      void queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.all(ecoRfpId),
        refetchType: "none",
      });
    }
  }, [ecoRfpId, queryClient]);

  const {
    isOpen: isRatesDrawerOpen,
    benefitPlanGroup: ratesDrawerBpg,
    benefitPlanId: ratesDrawerBpId,
    enableRateSets: ratesDrawerEnableRateSets,
    open: openRatesDrawer,
    close: closeRatesDrawer,
  } = useRatesDrawerState();
  // RatesGrid builds its header menus, and so its column defs, from this.
  const handleOpenRatesDrawer = useCallback<
    NonNullable<ComponentProps<typeof RatesGrid>["onOpenDrawer"]>
  >(
    (ctx) =>
      openRatesDrawer(
        ctx.benefitPlanGroup,
        ctx.benefitPlan,
        ctx.enableRateSets,
      ),
    [openRatesDrawer],
  );

  const quotedEnrollmentReview = useQuotedEnrollmentReview({
    rfpId: ecoRfpId,
    ratesGridData,
    onSaveError: (err) =>
      createToastNotification({
        severity: "error",
        message: `Failed to save enrollment choice: ${err.message}`,
      }),
  });

  // Pad and placeholder plans (empty slots / awaiting-quote columns) have no
  // backing proposal_plan row, so BTX rejects a rate-schedule PUT against their
  // synthetic FE id. Their "Edit Rate Structure" menu item is disabled until
  // the analyst materializes a real plan by entering a benefit value first.
  const disabledRatePlanIds = useMemo(
    () =>
      new Set([
        ...(placeholderMaterializeByPlanId?.keys() ?? []),
        ...(padFillByPlanId?.keys() ?? []),
      ]),
    [placeholderMaterializeByPlanId, padFillByPlanId],
  );

  const onRiderChargeSaveError = useCallback(
    (err: Error) => {
      createToastNotification({
        severity: "error",
        message: `Failed to save rider charge: ${err.message}`,
      });
    },
    [createToastNotification],
  );

  // Critical Illness only: Employee/Spouse/Child(ren) Rider Charge rows above
  // the premiums. A pad or placeholder slot has no plan to save them on yet.
  const riderChargeEditor = useRiderChargeEditor({
    rfpId: ecoRfpId,
    axiosInstance,
    scope: proposalScope,
    coverageType: selectedCoverageType,
    ratesGridData,
    unsavedPlanIds: disabledRatePlanIds,
    onSaveError: onRiderChargeSaveError,
  });

  const popupCustomTextOptions = useMemo(
    () => ({
      customTextByCellKey: popupCustomTextByCellKey,
      onCustomTextChange: handleCustomTextChangeMaterializing,
    }),
    [handleCustomTextChangeMaterializing, popupCustomTextByCellKey],
  );

  // Editable Plan Option header cell (CD-1261): dropdown of PlanOptionNameEnum
  // (persisted to proposal_plan.optionName) plus free-text override. Read-only
  // for the synthetic Incumbent/Requested carrier.
  const planOptionEditorApi = useMemo<PlanOptionEditorApi>(() => {
    const readOnlyPlanIds = new Set<string>(
      (carrierGridData?.carrierGroups ?? [])
        .filter((cg) => cg.carrierId === REQUESTED_CARRIER_ID)
        .flatMap((cg) =>
          cg.benefitPlanGroups.flatMap((g) =>
            (g.benefitPlans ?? []).map((p) => p.id),
          ),
        ),
    );
    const optionCellKey = (planId: string): string =>
      buildPopupCustomTextCellKey({
        benefitPlanId: planId,
        benefitKey: "optionName",
      } as PopupCustomTextFieldIdentity);
    const labels: Record<string, string> = {
      [PlanOptionNameEnum.high]: "High",
      [PlanOptionNameEnum.medium]: "Medium",
      [PlanOptionNameEnum.low]: "Low",
      [PlanOptionNameEnum.notApplicable]: "Not Applicable",
    };
    // Live enum option per plan, so an edit renders after refetch (AG Grid
    // won't refresh the static header-group param). notApplicable renders blank.
    const optionNameByPlanId = new Map<string, string>(
      (carrierGridData?.carrierGroups ?? []).flatMap((cg) =>
        cg.benefitPlanGroups.flatMap((g) =>
          (g.benefitPlans ?? []).map(
            (p) => [p.id, (p.optionName as string | undefined) ?? ""] as const,
          ),
        ),
      ),
    );
    const labelFor = (optionName: string | undefined): string =>
      optionName && optionName !== PlanOptionNameEnum.notApplicable
        ? (labels[optionName] ?? optionName)
        : "";
    return {
      options: Object.values(PlanOptionNameEnum).map((value) => ({
        value,
        label: labels[value] ?? value,
      })),
      onSelectOption: (planId, optionName) => {
        // Placeholder / pad columns have no real proposal_plan row yet — route
        // through the same create paths the benefit cells use so setting the
        // option materializes (or adds) the plan, exactly like a benefit edit.
        const padTarget = padFillByPlanId?.get(planId);
        if (padTarget) {
          addPlanToGroup(padTarget, undefined, {
            optionName: optionName as RfpBenefitPlan["optionName"],
          });
          return;
        }
        const materializeTarget = placeholderMaterializeByPlanId?.get(planId);
        if (materializeTarget) {
          materialize(materializeTarget, undefined, {
            optionName: optionName as RfpBenefitPlan["optionName"],
          });
          return;
        }
        void axiosInstance
          .patch(`btx/v1/proposal-plans/${planId}`, { optionName })
          .then(() => {
            if (ecoRfpId) {
              void queryClient.invalidateQueries({
                queryKey: proposalQueryKeys.all(ecoRfpId),
              });
            }
          })
          .catch((err: Error) => {
            createToastNotification({
              severity: "error",
              message: `Failed to save plan option: ${err.message}`,
            });
          });
      },
      onCustomTextChange: (planId, customText) =>
        handleCustomTextChangeMaterializing(optionCellKey(planId), customText),
      getCustomText: (planId) =>
        popupCustomTextByCellKey.get(optionCellKey(planId)),
      getOptionLabel: (planId) => labelFor(optionNameByPlanId.get(planId)),
      getOptionValue: (planId) => {
        const optionName = optionNameByPlanId.get(planId);
        return optionName && optionName !== PlanOptionNameEnum.notApplicable
          ? optionName
          : "";
      },
      isEditable: (planId) => !readOnlyPlanIds.has(planId),
      isEdited: (planId) => getEdited(planId, "optionName"),
      markEdited: (planId) => setEdited(planId, "optionName"),
    };
  }, [
    carrierGridData,
    axiosInstance,
    ecoRfpId,
    queryClient,
    createToastNotification,
    handleCustomTextChangeMaterializing,
    popupCustomTextByCellKey,
    getEdited,
    setEdited,
    placeholderMaterializeByPlanId,
    padFillByPlanId,
    materialize,
    addPlanToGroup,
  ]);

  // The configuration's Coma setting sits in the same sheet map as its row
  // toggles, so it comes back in the shown set when it is on.
  const comaInclusionOnly =
    useVisibleMpConfigKeys(ecoRfpId, selectedCoverageType)?.has(
      COMA_INCLUSION_ONLY_KEY,
    ) ?? false;

  // Override factories keyed on coverage type
  const {
    cellFormatOverride,
    cellEditorOverride,
    cellViewRendererOverride,
    collapseAmountRows,
  } = useQuotesOverrides(
    selectedCoverageType,
    popupCustomTextOptions,
    comaInclusionOnly,
  );

  // A materialized column is a real plan, so buildRowData no longer suppresses
  // the coverage's definition defaults for it and untouched cells read as
  // answered (Critical Illness showed "Not Included" for Benefit Maximum -
  // Spouse over a NULL column). Blank the cells with no stored value, after the
  // collapse transform so this sees the final row set.
  // Reads the coverage at call time rather than closing over it: PlanEditor
  // rebuilds rows whenever transformRowData's identity changes, so binding this
  // to the coverage object would rebuild the grid again on every save.
  const transformQuotesRowData = useMemo(
    () =>
      (rowData: unknown[]): unknown[] =>
        buildSuppressSeededDefaults(getCurrentCoverage())(
          collapseAmountRows(rowData),
        ),
    [collapseAmountRows, getCurrentCoverage],
  );

  // Always mount CellEditorOverrideProvider so activeOverride is wired before
  // the grid initialises. Fall back to a no-op while selectedCoverageType is
  // still undefined to avoid a race on first click.
  const noopCellEditorOverride = useCallback(() => undefined, []);
  const effectiveCellEditorOverride =
    cellEditorOverride ?? noopCellEditorOverride;

  const loadingState = getQuotesLoadingState({
    isLoading,
    error,
    hasData,
    selectedCoverage,
  });
  if (loadingState) return loadingState;

  return (
    <QuotesRatesErrorProvider>
      <PlanDesignerProvider
        configuration={{
          axiosInstance,
          rfpId,
          ecoRfpId,
        }}
      >
        <NotificationProvider>
          <Stack spacing={2}>
            {/* Toast + refresh the grid when an uploaded proposal finishes AI
              extraction, so the broker no longer has to hard-refresh. */}
            <ProposalExtractionToaster ecoRfpId={ecoRfpId} rfpId={rfpId} />
            <UnmappedPlansBannerSection
              carrierNames={carrierNamesWithUnmappedPlans}
            />
            <Stack spacing={2} sx={getFullScreenOverlaySx(isFullScreen)}>
              <Box ref={toolbarRef} sx={getStickyToolbarSx(isFullScreen)}>
                <QuotesToolbar
                  ecoRfpId={ecoRfpId}
                  brokerPortalRfpId={rfpId}
                  benefitPlanGroupsByCoverage={benefitPlanGroupsByCoverage}
                  availableCoverageTypes={availableCoverageTypes}
                  isLoadingAvailableCoverageTypes={
                    isLoadingAvailableCoverageTypes
                  }
                  unmappedCountByCoverage={unmappedCountByCoverage}
                  selectedCoverageType={selectedCoverageType}
                  selectedBpgId={selectedBpgId}
                  onPlanSelect={handlePlanSelectWithUrl}
                  columnWidth={columnWidth}
                  onColumnWidthChange={setColumnWidth}
                  isFullScreen={isFullScreen}
                  onToggleFullScreen={() => setIsFullScreen((prev) => !prev)}
                  showLegend={showLegend}
                  onShowLegendChange={setShowLegend}
                  showHistory={effectiveShowHistory}
                  onHistoryButtonClick={handleHistoryButtonClick}
                  onHistoryButtonPrefetch={prefetchHistoryProposals}
                  onAIExtract={() => setShowUploadDocumentsDrawer(true)}
                />
              </Box>

              <QuotesFullScreenPopupStyles active={isFullScreen} />
              {carrierGridData ? (
                <Box data-testid="quotes-grid" sx={GRID_CONTAINER_SX}>
                  {showLegend && <QuotesLegend />}
                  <NotificationProvider>
                    <DialogProvider>
                      <ValidationProvider>
                        <CellReviewProvider
                          getReview={getReview}
                          upsertReview={upsertReview}
                          clearReview={clearReview}
                          getBrokerHighlight={getBrokerHighlight}
                          setBrokerHighlight={setBrokerHighlight}
                          clearBrokerHighlight={clearBrokerHighlight}
                          getEdited={getEdited}
                          setEdited={setEdited}
                        >
                          <FieldMetadataProvider
                            ecoRfpId={ecoRfpId}
                            benefitPlanGroups={planEditorBenefitPlanGroups}
                          >
                            <SbsCellStateProvider
                              initialState={{}}
                              onDismissDifference={handleDismissDifference}
                            >
                              <AiFieldLookupProvider fieldLookup={fieldLookup}>
                                <AiCellStateSyncer
                                  cellStateMap={cellStateMap}
                                />
                                <DifferenceCellStateSyncer
                                  differenceMap={differenceMap}
                                />
                                <PlanOptionEditorProvider
                                  api={planOptionEditorApi}
                                >
                                  <QuotesPlanEditorPanel
                                    gridRef={planEditorGridRef}
                                    alignedGrids={[
                                      ratesGridRef,
                                      summaryGridRef,
                                    ]}
                                    onGridReady={
                                      handlePlanEditorGridReadyWithHiddenColumns
                                    }
                                    isMutating={isRefetchingProposals}
                                    columnWidthScale={columnWidthScale}
                                    coverage={planEditorCoverage}
                                    getCurrentCoverage={getCurrentCoverage}
                                    benefitPlanGroups={
                                      planEditorBenefitPlanGroups
                                    }
                                    configuration={{
                                      autoHeight: true,
                                      stickyHeader: true,
                                      stickyHeaderTop: getStickyHeaderTop(
                                        isFullScreen,
                                        toolbarHeight,
                                      ),
                                      carrierGroups:
                                        carrierGridData.carrierGroups,
                                      carrierGroupedBenefitsHeaderLabels:
                                        CARRIER_GROUPED_BENEFITS_HEADER_LABELS,
                                      currentCarrierId: REQUESTED_CARRIER_ID,
                                      editableNonCurrentCarriers: true,
                                      placeholderMaterializeByPlanId:
                                        carrierGridData.placeholderMaterializeByPlanId,
                                      padFillByPlanId:
                                        carrierGridData.padFillByPlanId,
                                      // Always shown (independent of the flag): CD-1730
                                      // already renders every version as its own column,
                                      // so labeling which is which is useful regardless
                                      // of whether requote review itself is live.
                                      showVersionRow: true,
                                      quoteHistoryEnabled,
                                    }}
                                    onCoverageChange={
                                      handleCoverageChangeWithToggleOverride
                                    }
                                    onCarrierReorder={setCarrierOrder}
                                    gridStateProps={quotesGridStatePropsBound}
                                    transformRowData={transformQuotesRowData}
                                    ecoRfpId={ecoRfpId}
                                    selectedCoverageType={selectedCoverageType}
                                    selectedBpgId={selectedBpgId}
                                    benefitPlanGroupsByCoverage={
                                      benefitPlanGroupsByCoverage
                                    }
                                    onMoveError={handleMoveError}
                                    sx={QUOTES_PLAN_EDITOR_SX}
                                    cellEditorOverride={
                                      effectiveCellEditorOverride
                                    }
                                    cellViewRendererOverride={
                                      cellViewRendererOverride
                                    }
                                    cellFormatOverride={cellFormatOverride}
                                  />
                                </PlanOptionEditorProvider>
                              </AiFieldLookupProvider>
                            </SbsCellStateProvider>
                          </FieldMetadataProvider>
                          {ratesGridData && (
                            <Box
                              sx={{
                                borderTop: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              <RatesGrid
                                ref={ratesGridRef}
                                alignedGrids={[
                                  planEditorGridRef,
                                  summaryGridRef,
                                ]}
                                onGridReady={trackCompanionGrid}
                                axiosInstance={axiosInstance}
                                carrierGroups={ratesGridData.carrierGroups}
                                benefitPlanGroups={
                                  ratesGridData.benefitPlanGroups
                                }
                                coverageType={selectedCoverageType}
                                columnWidthScale={columnWidthScale}
                                columnWidths={planEditorColumnWidths}
                                onRateSaved={handleRateSaved}
                                readOnlyCarrierIds={READ_ONLY_CARRIER_IDS}
                                rateSetsCarrierIds={READ_ONLY_CARRIER_IDS}
                                aiFieldsByPlanId={aiFieldsByPlanId}
                                disabledPlanIds={disabledRatePlanIds}
                                disabledPlanTooltip={DISABLED_RATE_EDIT_TOOLTIP}
                                illustratedFrom={illustratedFrom}
                                onOpenDrawer={handleOpenRatesDrawer}
                                onReviewQuotedEnrollment={
                                  quotedEnrollmentReview.open
                                }
                              />
                              <SummaryGrid
                                ref={summaryGridRef}
                                alignedGrids={[planEditorGridRef, ratesGridRef]}
                                onGridReady={trackCompanionGrid}
                                axiosInstance={axiosInstance}
                                carrierGroups={ratesGridData.carrierGroups}
                                benefitPlanGroups={
                                  ratesGridData.benefitPlanGroups
                                }
                                columnWidthScale={columnWidthScale}
                                columnWidths={planEditorColumnWidths}
                                onFootnoteChange={handleFootnoteChange}
                                footnoteHeaderLabel={footnoteHeaderLabel}
                                onFootnoteHeaderLabelChange={
                                  onFootnoteHeaderLabelChange
                                }
                                placeholderMaterializeByPlanId={
                                  carrierGridData?.placeholderMaterializeByPlanId
                                }
                                riderChargeEditor={riderChargeEditor}
                              />
                            </Box>
                          )}
                        </CellReviewProvider>
                      </ValidationProvider>
                      <CellReviewCommentDialogRenderer />
                    </DialogProvider>
                  </NotificationProvider>
                </Box>
              ) : (
                <Alert severity="info">
                  No carrier quotes available for this coverage.
                </Alert>
              )}
            </Stack>
          </Stack>

          <QuotesDialogsAndDrawers
            historyDialogOpen={historyDialogOpen}
            onHistoryDialogClose={() => setHistoryDialogOpen(false)}
            historyCarrierOptions={historyCarrierOptions}
            isLoadingCarriers={isLoadingCarriers}
            selectedHistoryCarrierIds={selectedHistoryCarrierIds}
            onHistoryView={handleHistoryView}
            carriers={carriers}
            ecoRfpId={ecoRfpId}
            brokerId={brokerId}
            brokerPortalRfpId={brokerPortalRfpId}
            showUploadDocumentsDrawer={showUploadDocumentsDrawer}
            onCloseUploadDocumentsDrawer={() =>
              setShowUploadDocumentsDrawer(false)
            }
            isRatesDrawerOpen={isRatesDrawerOpen}
            onCloseRatesDrawer={closeRatesDrawer}
            onRateSaved={handleRateSaved}
            ratesDrawerBpg={ratesDrawerBpg}
            ratesDrawerBpId={ratesDrawerBpId}
            ratesDrawerEnableRateSets={ratesDrawerEnableRateSets}
            ratesDrawerCoverageType={selectedCoverageType}
            axiosInstance={axiosInstance}
            enrollmentReview={quotedEnrollmentReview}
          />
        </NotificationProvider>
      </PlanDesignerProvider>
    </QuotesRatesErrorProvider>
  );
};

export default Quotes;
